import { CSSProperties, FC } from 'react';
import { Descriptions } from 'antd';
import type { DescriptionsProps } from 'antd';
import { TbInfoCircle } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import SmallTooltip from '@/shared/components/tooltips/SmallTooltip';
import { ellipsisStyling } from '@/utilities';

import { FinancialOverviewDescriptionItem } from '../../types';

import './index.css';

interface FinancialOverviewDescriptionsProps {
    className?: string;
    column?: DescriptionsProps['column'];
    items: FinancialOverviewDescriptionItem[];
    labelColor?: CSSProperties['color'];
    style?: CSSProperties;
}

const defaultColumns: DescriptionsProps['column'] = { xs: 2, sm: 3, md: 4, lg: 6, xl: 6, xxl: 6 };

export const FinancialOverviewDescriptions: FC<FinancialOverviewDescriptionsProps> = ({
    className,
    column = defaultColumns,
    items,
    labelColor,
    style,
}) => {
    const { token } = useAntDesignTheme();
    const descriptionsStyle: CSSProperties &
        Record<'--financial-overview-description-pair-gap' | '--financial-overview-description-row-gap', string> = {
        '--financial-overview-description-pair-gap': `${token.paddingXXS / 2}px`,
        '--financial-overview-description-row-gap': `${token.paddingMD}px`,
        ...style,
    };

    return (
        <Descriptions
            className={['financial-overview-descriptions', className].filter(Boolean).join(' ')}
            colon={false}
            column={column}
            items={items.map((item) => ({
                children: <div style={{ ...ellipsisStyling, color: token.colorText }}>{item.value || '-'}</div>,
                key: item.key,
                label: (
                    <div
                        style={{
                            alignItems: 'center',
                            display: 'flex',
                            gap: '0.25rem',
                            minWidth: 0,
                            width: '100%',
                        }}
                    >
                        <small
                            style={{
                                color: labelColor ?? token.colorTextSecondary,
                                flex: '0 1 auto',
                                minWidth: 0,
                                ...ellipsisStyling,
                            }}
                        >
                            {item.icon ? <span style={{ marginRight: '0.25rem' }}>{item.icon}</span> : null}
                            {item.label}
                        </small>
                        {item.description
                            ? (item.tooltip ?? (
                                  <SmallTooltip placement='bottomRight' showInfo={false} title={item.description}>
                                      <TbInfoCircle
                                          aria-label={`${String(item.label)} definition`}
                                          size='0.875rem'
                                          style={{ color: token.colorTextTertiary, display: 'block', flex: 'none' }}
                                      />
                                  </SmallTooltip>
                              ))
                            : null}
                        {item.action ? (
                            <span style={{ display: 'inline-flex', flex: 'none', marginLeft: 'auto' }}>
                                {item.action}
                            </span>
                        ) : null}
                    </div>
                ),
            }))}
            layout='vertical'
            style={descriptionsStyle}
            styles={{
                content: { minWidth: 0 },
                label: { minWidth: 0 },
            }}
        />
    );
};

export type { FinancialOverviewDescriptionsProps };
